import React from "react";
import {
  FaUsers,
  FaMusic,
  FaBullseye,
  FaEnvelope,
} from "react-icons/fa";
import { FaPeopleGroup } from "react-icons/fa6";

/* ------------------------------------------------------------------ */
/*  Dati                                                               */
/* ------------------------------------------------------------------ */
const linkDinardo = (
  <a
    href="https://www.giuseppedinardo.it/"
    className="text-red-600 font-semibold hover:underline"
    target="_blank"
    rel="noopener noreferrer"
  >
    Giuseppe Dinardo
  </a>
);

// Il percorso di MilArte, in ordine di tempo
const percorso = [
  {
    quando: "2023",
    titolo: "Storia e Background",
    testo: (
      <>
        Nata dalla visione di {linkDinardo}, MilArte è stata fondata per
        diffondere la cultura musicale. La nostra presenza nel panorama
        musicale locale cresce grazie alla dedizione dei nostri membri e al
        sostegno della comunità.
      </>
    ),
  },
  {
    quando: "Dicembre 2023",
    titolo: "Successi e Realizzazioni",
    testo: (
      <>
        Nel dicembre 2023 abbiamo organizzato concerti di grande successo,
        ricevendo apprezzamento dalla comunità. Ma questo è solo l’inizio!
      </>
    ),
  },
  {
    quando: "Il futuro",
    titolo: "Progetti Futuri",
    testo: (
      <>
        MilArte guarda al futuro con entusiasmo: nuovi eventi, collaborazioni
        e progetti educativi per diffondere la cultura musicale in modo
        innovativo.
      </>
    ),
  },
];

const cosaFacciamo = [
  {
    icon: <FaBullseye />,
    color: "text-amber-700 bg-amber-100",
    titolo: "Obiettivi e Missioni",
    testo:
      "Proponiamo concerti, eventi e manifestazioni, convinti che la musica possa trasformare e arricchire la vita di tutti. Lavoriamo per rendere l’arte musicale accessibile e coinvolgente per tutti.",
  },
  {
    icon: <FaMusic />,
    color: "text-red-600 bg-red-50",
    titolo: "Attività",
    testo:
      "Offriamo programmi, laboratori e iniziative per far vivere esperienze musicali autentiche alla comunità, favorendo l’incontro tra professionisti e appassionati.",
  },
];

/* ------------------------------------------------------------------ */
/*  Pagina                                                             */
/* ------------------------------------------------------------------ */
const ChiSiamo = () => {
  return (
    <div className="flex flex-col items-center w-full min-h-screen bg-gradient-to-r from-white to-amber-50 text-gray-800 overflow-hidden">
      <FaPeopleGroup className="fixed text-amber-200/40 text-[180px] rotate-12 -top-5 -right-5 z-0" />

      <div className="relative z-10 w-11/12 md:w-3/4 lg:w-2/3 max-w-4xl animate__animated animate__fadeIn">
        {/* --- INTESTAZIONE + INTRO --- */}
        <header className="mt-10 mb-16">
          <h1 className="text-red-600 font-extrabold text-4xl md:text-5xl tracking-wide">
            Chi siamo
          </h1>
          <p className="mt-4 text-2xl md:text-3xl font-semibold text-amber-900 leading-snug">
            Promuoviamo la Passione per la Musica, Ispiriamo la Comunità
          </p>
          <p className="mt-6 text-lg md:text-xl leading-relaxed text-gray-700">
            Siamo lieti di presentarvi{" "}
            <span className="font-semibold">MilArte</span>, un'Associazione di
            Promozione Sociale dedicata alla musica. Fondata nel{" "}
            <strong>2023</strong>, ci impegniamo a diffondere la passione per
            la musica e a creare un impatto positivo nella nostra comunità
            attraverso iniziative musicali significative.
          </p>
        </header>

        {/* --- PERCORSO --- */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-red-600 mb-8">
            Il nostro percorso
          </h2>
          <ol className="border-l-2 border-red-200 ml-2 space-y-10">
            {percorso.map((tappa) => (
              <li key={tappa.titolo} className="relative pl-8">
                <span
                  className="absolute -left-[9px] top-1.5 w-4 h-4 rounded-full bg-red-600 ring-4 ring-amber-50"
                  aria-hidden="true"
                />
                <p className="text-red-600 font-bold text-lg">{tappa.quando}</p>
                <h3 className="text-xl md:text-2xl font-semibold text-gray-900">
                  {tappa.titolo}
                </h3>
                <p className="mt-2 text-lg leading-relaxed text-gray-700">
                  {tappa.testo}
                </p>
              </li>
            ))}
          </ol>
        </section>

        {/* --- COSA FACCIAMO --- */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-red-600 mb-8">
            Cosa facciamo
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {cosaFacciamo.map((voce) => (
              <div key={voce.titolo} className="flex items-start gap-5">
                <span
                  className={`shrink-0 flex items-center justify-center w-14 h-14 rounded-full text-2xl ${voce.color}`}
                >
                  {voce.icon}
                </span>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {voce.titolo}
                  </h3>
                  <p className="mt-1 text-lg leading-relaxed text-gray-700">
                    {voce.testo}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* --- MEMBRI E STRUTTURA --- */}
        <section className="mb-16 bg-amber-100/70 border border-amber-200 rounded-3xl p-8 md:p-12 flex flex-col md:flex-row items-center md:items-start gap-6 text-center md:text-left">
          <span className="shrink-0 flex items-center justify-center w-16 h-16 rounded-full bg-white text-red-600 text-3xl shadow-sm">
            <FaUsers />
          </span>
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              Membri e Struttura
            </h2>
            <p className="mt-3 text-lg leading-relaxed text-gray-700">
              Il cuore pulsante di MilArte è il nostro team, guidato dal
              Presidente {linkDinardo}, che con passione e dedizione guida
              l’associazione verso nuovi traguardi.
            </p>
          </div>
        </section>
      </div>

      {/* --- CONTATTI --- */}
      <section className="relative z-10 w-full bg-red-700 text-white py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-amber-200">Contatti</h2>
          <p className="mt-4 text-xl leading-relaxed">
            Vuoi unirti a noi nel nostro viaggio musicale? Scrivici o seguici
            sui social per rimanere aggiornato sulle attività.
          </p>
          <a
            href="mailto:milarte.mi@gmail.com"
            className="mt-8 inline-flex items-center gap-2 bg-white text-red-700 font-semibold px-6 py-3 rounded-lg hover:bg-amber-100 transition focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-200"
          >
            <FaEnvelope />
            Scrivici
          </a>
          <p className="mt-8 font-extrabold text-lg md:text-xl text-amber-100">
            Grazie per il vostro tempo e il vostro sostegno!
          </p>
        </div>
      </section>
    </div>
  );
};

export default ChiSiamo;